import { z } from 'zod';

import { subspecialtySchema } from './specialty.schema';

const optionalString = z.string().optional();

// API returns empty strings. We should allow empty strings but convert them to undefined.
const optionalUrl = z
  .url()
  .or(z.literal(''))
  .transform((v) => (v === '' ? undefined : v))
  .optional();

const optionalEmail = z
  .email()
  .or(z.literal(''))
  .transform((v) => (v === '' ? undefined : v))
  .optional();

/**
 * Schema for Promotion's SpecialtySerializer from API.
 * Note: The API defines a specific serializer for this, rather that using the SpecialtySerializer like subspectialties.
 */
export const promotionSpecialtySchema = z.object({
  specialtyId: z.number(),
  specialtyName: z.string(),
});

/**
 * Schema for promotion location. PromotionLocationSerializer from API.
 */
export const promotionLocationSchema = z.looseObject({
  address1: optionalString,
  address2: optionalString,
  landmarkGuidance: optionalString,
  city: optionalString,
  state: optionalString,
  zipcode: optionalString,
  phone: optionalString,
  fax: optionalString,
  name: optionalString,
  latitude: z.coerce.number().optional(),
  longitude: z.coerce.number().optional(),
  npi: optionalString,
});

/**
 * Schema for Promotion API response (camelCase API response).
 */
export const promotionSchema = z.looseObject({
  id: z.number(),
  client: optionalString,
  programName: optionalString,
  programSubname: optionalString,
  programDescription: optionalString,
  detailedDescription: optionalString,
  notificationText: optionalString,
  whenToUse: z.array(z.string()).optional(),
  accessAppLink: optionalUrl,
  accessChatLink: optionalUrl,
  getStartedLink: optionalUrl,
  accessEmail: optionalEmail,
  pdfLink: optionalUrl,
  costText: optionalString,
  costBadgeText: optionalString,
  ratingIcon: optionalUrl,
  employeeRating: optionalString,
  website: optionalUrl,
  cardSubtext: optionalString,
  phoneNumber: optionalString,
  fax: optionalString,
  costIcon: optionalUrl,
  logo: optionalUrl,
  badge1: optionalUrl,
  badge1Text: optionalString,
  badge2: optionalUrl,
  badge2Text: optionalString,
  badge3: optionalUrl,
  badge3Text: optionalString,
  telehealthAvailable: z.boolean(),
  weight: z.number(),
  sortOrder: z.number().optional(),
  specialties: z.array(promotionSpecialtySchema),
  subspecialties: z.array(subspecialtySchema),
  locations: z.array(promotionLocationSchema),
  radius: z.number(),
  isPrimary: z.boolean(),
  hideOnLandingPage: z.boolean(),
});

export const promotionListSchema = z.array(promotionSchema);

export type Promotion = z.infer<typeof promotionSchema>;
export type PromotionList = z.infer<typeof promotionListSchema>;
